/**
 * CoherenceMeter Component
 * Visual display of constellation coherence score
 */

interface CoherenceMeterProps {
  score: number; // 0-1
  showLabel?: boolean;
}

const getCoherenceInfo = (score: number) => {
  if (score < 0.4) {
    return {
      label: 'Wild',
      color: 'bg-orange-500',
      textColor: 'text-orange-600',
      description: 'Diverse exploration across styles',
    };
  }
  if (score > 0.7) {
    return {
      label: 'Tight',
      color: 'bg-green-500',
      textColor: 'text-green-600',
      description: 'Focused within a specific style',
    };
  }
  return {
    label: 'Balanced',
    color: 'bg-blue-500',
    textColor: 'text-blue-600',
    description: 'Mix of coherence and variety',
  };
};

export default function CoherenceMeter({ score, showLabel = true }: CoherenceMeterProps) {
  const coherence = getCoherenceInfo(score);

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">Coherence</span>
        <span className={`text-lg font-bold ${coherence.textColor}`}>
          {coherence.label}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
        <div
          className={`h-full transition-all duration-500 ${coherence.color}`}
          style={{ width: `${score * 100}%` }}
        />
      </div>

      {/* Description */}
      {showLabel && (
        <p className="text-xs text-gray-500">{coherence.description}</p>
      )}

      {/* Visual Indicators */}
      <div className="flex justify-between mt-3 text-xs text-gray-400">
        <span className={score < 0.4 ? coherence.textColor : ''}>Wild</span>
        <span className={score >= 0.4 && score <= 0.7 ? coherence.textColor : ''}>
          Balanced
        </span>
        <span className={score > 0.7 ? coherence.textColor : ''}>Tight</span>
      </div>
    </div>
  );
}
